import React, { useState, useEffect, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Search, Plus, Pencil, Star, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

export default function Clientes() {
  const [clientes, setClientes] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const cargar = useCallback(async () => {
    const { data } = await api.get(`/clientes?search=${encodeURIComponent(search)}`);
    setClientes(data);
  }, [search]);

  useEffect(() => { const tt = setTimeout(cargar, 200); return () => clearTimeout(tt); }, [cargar]);

  const abrir = (c) => { setEditing(c); setOpen(true); };

  return (
    <div className="p-4 lg:p-8 space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-bold text-[26px] lg:text-[30px] leading-[1.05] text-ink" data-testid="page-title">{t("Clientes")}</h1>
        <Button data-testid="cliente-nuevo-btn" className="bg-brand hover:bg-brand-dark text-white" onClick={() => abrir(null)}><Plus className="h-4 w-4 mr-2" />{t(" Nuevo cliente")}</Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-3" />
        <Input data-testid="cliente-buscador" placeholder={t("Buscar cliente…")} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10 bg-white" />
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-2 text-left eyebrow">
              <th className="px-4 py-3">{t("Nombre")}</th>
              <th className="px-4 py-3">{t("Dirección")}</th>
              <th className="px-4 py-3">{t("Frecuente")}</th>
              <th className="px-4 py-3">{t("Estado")}</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#EFECE6]">
            {clientes.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-ink-3">{t("Sin clientes")}</td></tr>}
            {clientes.map((c) => (
              <tr key={c.id} data-testid={`cliente-fila-${c.id}`} className={cn("hover:bg-surface-2", !c.activo && "opacity-50")}>
                <td className="px-4 py-3 font-semibold text-ink flex items-center gap-1">{c.frecuente && <Star className="h-3.5 w-3.5 fill-gold text-gold" />}{c.nombre}</td>
                <td className="px-4 py-3 text-ink-2">{c.direccion || "—"}</td>
                <td className="px-4 py-3">{c.frecuente ? t("Sí") : t("No")}</td>
                <td className="px-4 py-3">{c.activo ? <span className="text-[#277A3F] font-medium">{t("Activo")}</span> : <span className="text-ink-3">{t("Inactivo")}</span>}</td>
                <td className="px-4 py-3 text-right"><Button data-testid={`cliente-editar-${c.id}`} variant="ghost" size="sm" onClick={() => abrir(c)}><Pencil className="h-4 w-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && <ClienteDialog cliente={editing} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); cargar(); }} />}
    </div>
  );
}

function ClienteDialog({ cliente, onClose, onSaved }) {
  const [nombre, setNombre] = useState(cliente?.nombre || "");
  const [direccion, setDireccion] = useState(cliente?.direccion || "");
  const [frecuente, setFrecuente] = useState(cliente?.frecuente || false);
  const [notas, setNotas] = useState(cliente?.notas || "");
  const [activo, setActivo] = useState(cliente ? cliente.activo : true);
  const [saving, setSaving] = useState(false);

  const guardar = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { nombre, direccion, frecuente, notas, activo };
      if (cliente) await api.put(`/clientes/${cliente.id}`, payload);
      else await api.post("/clientes", payload);
      toast.success(cliente ? t("Cliente actualizado") : t("Cliente creado"));
      onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>{cliente ? t("Editar cliente") : t("Nuevo cliente")}</DialogTitle></DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          <div><Label className="eyebrow">{t("Nombre *")}</Label><Input data-testid="cliente-nombre-input" value={nombre} onChange={(e) => setNombre(e.target.value)} className="mt-1 h-11" required /></div>
          <div><Label className="eyebrow">{t("Dirección")}</Label><Input value={direccion} onChange={(e) => setDireccion(e.target.value)} className="mt-1 h-11" /></div>
          <div><Label className="eyebrow">{t("Notas")}</Label><Textarea value={notas} onChange={(e) => setNotas(e.target.value)} className="mt-1" rows={2} /></div>
          <label className="flex items-center gap-2 text-sm text-ink-2"><input data-testid="cliente-frecuente-check" type="checkbox" checked={frecuente} onChange={(e) => setFrecuente(e.target.checked)} className="h-4 w-4" />{t(" Cliente frecuente")}</label>
          {cliente && <label className="flex items-center gap-2 text-sm text-ink-2"><input data-testid="cliente-activo-check" type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} className="h-4 w-4" />{t(" Activo")}</label>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>{t("Cancelar")}</Button>
            <Button type="submit" data-testid="cliente-guardar" className="bg-brand hover:bg-brand-dark text-white" disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Guardar")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
